/**
 * Enums for wrapped node change broadcasting system.
 *
 * These enums categorize property changes on wrapped nodes so that wrap nodes
 * can efficiently respond to changes in their contained nodes.
 *
 * The change broadcasting system allows nodes to notify their containing wrap nodes
 * when properties change, enabling bidirectional synchronization between Start/End pairs.
 *
 * @module WrappedNodeChangeEnums
 * @see SequencerNode._broadcastChangeToWrapNodes
 * @see WrapSequencerNode.onWrappedNodeChanged
 */

/**
 * Listener mode for wrap nodes.
 * Determines whether and how a wrap node listens to changes in wrapped nodes.
 *
 * @readonly
 * @enum {string}
 */
export const WrappedNodeChangeListenerMode = {
    /**
     * Don't listen to wrapped node changes (default).
     * The wrap node will not receive any change notifications.
     */
    NONE: 'NONE',

    /**
     * Listen to all property changes on wrapped nodes.
     * Every change triggers onWrappedNodeChanged callback.
     */
    ALL: 'ALL',

    /**
     * Listen only to specific property categories.
     * The categories are defined by getListenedPropertyCategories().
     */
    SPECIFIC_PROPS: 'SPECIFIC_PROPS'
};

/**
 * Categories of properties that can change on a wrapped node.
 * Used to help wrap nodes efficiently filter and respond to relevant changes.
 *
 * @readonly
 * @enum {string}
 */
export const WrappedNodePropertyCategory = {
    /**
     * Changes to the audio segment (start time, end time, duration, content).
     * These affect audio scheduling and rendering.
     */
    AUDIO_SEGMENT: 'AUDIO_SEGMENT',

    /**
     * Changes to video-related properties (video URL, dimensions, effects).
     * These affect visual rendering.
     */
    VIDEO: 'VIDEO',

    /**
     * Changes to node position in the timeline (next/previous node links).
     * These affect the range of wrapped content.
     */
    POSITION: 'POSITION',

    /**
     * Changes to visual display properties (selection, highlighting, etc.).
     * These are typically UI-only and don't affect rendering.
     */
    VISUAL: 'VISUAL',

    /**
     * Changes to timing properties (duration, start time, end time).
     * These affect scheduling and rendering.
     */
    TIMING: 'TIMING',

    /**
     * Changes to speaker information.
     * These may affect face detection and speaker-based effects.
     */
    SPEAKER: 'SPEAKER',

    /**
     * Other property changes that don't fit the above categories.
     */
    OTHER: 'OTHER'
};

/**
 * Mapping from Backbone model property names to their categories.
 * Used by _enableChangeBroadcasting() to categorize change events.
 *
 * @readonly
 * @type {Object.<string, string>}
 */
export const PROPERTY_TO_CATEGORY = {
    // Audio segment changes
    'audioSegment': WrappedNodePropertyCategory.AUDIO_SEGMENT,

    // Position changes
    '_nextSequenceNodeCid': WrappedNodePropertyCategory.POSITION,
    '_previousSequenceNodeCid': WrappedNodePropertyCategory.POSITION,

    // Video/media changes
    'video_crop': WrappedNodePropertyCategory.VIDEO,
    'crop_aspect_ratio': WrappedNodePropertyCategory.VIDEO,
    'mediaUrl': WrappedNodePropertyCategory.VIDEO,
    'mediaType': WrappedNodePropertyCategory.VIDEO,
    'object_fit': WrappedNodePropertyCategory.VIDEO,

    // Timing changes
    'startTime': WrappedNodePropertyCategory.TIMING,
    'endTime': WrappedNodePropertyCategory.TIMING,
    'duration': WrappedNodePropertyCategory.TIMING,

    // Speaker changes
    'speaker': WrappedNodePropertyCategory.SPEAKER,
    'speakerId': WrappedNodePropertyCategory.SPEAKER,
    'speakerInfo': WrappedNodePropertyCategory.SPEAKER,

    // Visual/UI changes (typically not broadcast to wrap nodes)
    'selected': WrappedNodePropertyCategory.VISUAL,
    'highlighted': WrappedNodePropertyCategory.VISUAL,
    'hoverHighlighted': WrappedNodePropertyCategory.VISUAL,
    'searchHightlighted': WrappedNodePropertyCategory.VISUAL,
    'playing': WrappedNodePropertyCategory.VISUAL
};

/**
 * Determines the category for a given property name.
 *
 * @param {string} propertyName - The name of the property that changed
 * @returns {string} The WrappedNodePropertyCategory value
 */
export function getPropertyCategory(propertyName) {
    // Check explicit mapping first
    if (PROPERTY_TO_CATEGORY[propertyName]) {
        return PROPERTY_TO_CATEGORY[propertyName];
    }

    // Check if property name indicates a video-related property
    if (propertyName) {
        const lowerName = propertyName.toLowerCase();
        if (lowerName.startsWith('video') || lowerName.includes('crop') || lowerName.includes('media')) {
            return WrappedNodePropertyCategory.VIDEO;
        }
        if (lowerName.includes('speaker')) {
            return WrappedNodePropertyCategory.SPEAKER;
        }
    }

    // Default to OTHER
    return WrappedNodePropertyCategory.OTHER;
}

// Default export for backward compatibility
const WrappedNodeChangeEnumsCompat = {
    WrappedNodeChangeListenerMode,
    WrappedNodePropertyCategory,
    PROPERTY_TO_CATEGORY,
    getPropertyCategory
};

export default WrappedNodeChangeEnumsCompat;
